import { useCallback, useEffect, useRef, useState } from 'react'

import { datadogRum } from '@datadog/browser-rum'

import { standardRecipeTileFetcher, type StandardRecipeItem } from '@library/api-menu-experience'
import type { RecipeURN, RecipeUUID } from '@library/menu-service'

type Override = {
  recipeTile: StandardRecipeItem
  fetchedNumPortions: number
  hasBeenInBasket: boolean
}

type InFlightRefresh = {
  key: string
  requestId: number
}

type Args = {
  boxNumPortions: number
  contextKey: string
  deliveryDate: string
  menuId: string
  getNumPortionsForRecipeId: (recipeId: RecipeUUID) => number
  getQuantitiesForRecipeId: (recipeId: RecipeUUID) => number
  onOverrideRemoved: (recipeId: RecipeUUID) => void
}

/**
 * Temporary frontend-owned store for replacement recipe tiles.
 * Remove or replace this when the backend-driven tile store provides confirmed tiles without
 * frontend basket and recipe-option inference.
 */
export const useRecipeGridItemOverrides = ({
  boxNumPortions,
  contextKey,
  deliveryDate,
  menuId,
  getNumPortionsForRecipeId,
  getQuantitiesForRecipeId,
  onOverrideRemoved,
}: Args) => {
  const [overrides, setOverrides] = useState<Partial<Record<RecipeUUID, Override>>>({})
  const contextKeyRef = useRef(contextKey)
  const getNumPortionsForRecipeIdRef = useRef(getNumPortionsForRecipeId)
  const inFlightRefreshRef = useRef(new Map<RecipeUUID, InFlightRefresh>())
  const refreshRequestIdRef = useRef(new Map<RecipeUUID, number>())

  contextKeyRef.current = contextKey
  getNumPortionsForRecipeIdRef.current = getNumPortionsForRecipeId

  useEffect(() => {
    inFlightRefreshRef.current.clear()
    setOverrides({})
  }, [contextKey])

  const removeOverride = useCallback(
    (recipeId: RecipeUUID) => {
      const entry = Object.entries(overrides).find(
        ([, override]) => override?.recipeTile.id === recipeId,
      ) as [RecipeUUID, Override] | undefined

      if (!entry) {
        return
      }

      const [originalRecipeId] = entry
      setOverrides((current) => {
        const next = { ...current }
        delete next[originalRecipeId]

        return next
      })
      inFlightRefreshRef.current.delete(originalRecipeId)
      onOverrideRemoved(recipeId)
    },
    [onOverrideRemoved, overrides],
  )

  const refreshOverride = useCallback(
    async (recipeId: RecipeUUID, numPortions: number) => {
      const entry = Object.entries(overrides).find(
        ([, override]) => override?.recipeTile.id === recipeId,
      ) as [RecipeUUID, Override] | undefined

      if (!entry) {
        return
      }

      const [originalRecipeId] = entry
      const refreshKey = `${recipeId}:${numPortions}`

      if (inFlightRefreshRef.current.get(originalRecipeId)?.key === refreshKey) {
        return
      }

      const requestId = (refreshRequestIdRef.current.get(originalRecipeId) ?? 0) + 1
      refreshRequestIdRef.current.set(originalRecipeId, requestId)
      inFlightRefreshRef.current.set(originalRecipeId, { key: refreshKey, requestId })
      const requestContextKey = contextKey

      try {
        const recipeTile = await standardRecipeTileFetcher({
          recipeId,
          menuId,
          numPortions,
          deliveryDate,
        })

        if (
          contextKeyRef.current !== requestContextKey ||
          requestId !== refreshRequestIdRef.current.get(originalRecipeId) ||
          getNumPortionsForRecipeIdRef.current(recipeId) !== numPortions
        ) {
          return
        }

        setOverrides((current) => {
          const currentOverride = current[originalRecipeId]

          return currentOverride?.recipeTile.id === recipeId
            ? {
                ...current,
                [originalRecipeId]: {
                  ...currentOverride,
                  recipeTile: {
                    ...recipeTile,
                    urn: recipeTile.urn ?? currentOverride.recipeTile.urn,
                  },
                  fetchedNumPortions: numPortions,
                },
              }
            : current
        })
      } catch {
        datadogRum.addAction('menu_experience_recipe_tile_refresh_failed', {
          menu_id: menuId,
          recipe_id: recipeId,
          num_portions: numPortions,
        })
        // Keep the last valid tile and allow a later state change to retry the refresh.
      } finally {
        if (inFlightRefreshRef.current.get(originalRecipeId)?.requestId === requestId) {
          inFlightRefreshRef.current.delete(originalRecipeId)
        }
      }
    },
    [contextKey, deliveryDate, menuId, overrides],
  )

  useEffect(() => {
    Object.entries(overrides).forEach(([originalRecipeId, override]) => {
      if (!override) {
        return
      }

      const recipeId = override.recipeTile.id
      const quantity = getQuantitiesForRecipeId(recipeId)

      if (override.hasBeenInBasket && quantity === 0) {
        removeOverride(recipeId)

        return
      }

      if (!override.hasBeenInBasket && quantity > 0) {
        setOverrides((current) => {
          const currentOverride = current[originalRecipeId as RecipeUUID]

          return currentOverride?.recipeTile.id === recipeId
            ? {
                ...current,
                [originalRecipeId]: { ...currentOverride, hasBeenInBasket: true },
              }
            : current
        })
      }

      const basketNumPortions = getNumPortionsForRecipeId(recipeId)

      if (override.fetchedNumPortions !== basketNumPortions) {
        void refreshOverride(recipeId, basketNumPortions)
      }
    })
  }, [
    boxNumPortions,
    getNumPortionsForRecipeId,
    getQuantitiesForRecipeId,
    overrides,
    refreshOverride,
    removeOverride,
  ])

  const applyOverride = useCallback(
    (originalRecipeId: RecipeUUID, recipeTile: StandardRecipeItem, numPortions: number) => {
      setOverrides((current) => ({
        ...current,
        [originalRecipeId]: {
          recipeTile,
          fetchedNumPortions: numPortions,
          hasBeenInBasket: getQuantitiesForRecipeId(recipeTile.id) > 0,
        },
      }))
    },
    [getQuantitiesForRecipeId],
  )

  const prepareOverride = useCallback(
    async (
      originalRecipeId: RecipeUUID,
      recipeId: RecipeUUID,
      numPortions: number,
      recipeUrn?: RecipeURN,
    ) => {
      const recipeTile = await standardRecipeTileFetcher({
        recipeId,
        menuId,
        numPortions,
        deliveryDate,
      })
      const selectedRecipeUrn = recipeTile.urn ?? recipeUrn
      const recipeTileWithUrn = { ...recipeTile, urn: selectedRecipeUrn }

      return {
        commit: () => applyOverride(originalRecipeId, recipeTileWithUrn, numPortions),
        selectedRecipeId: recipeTile.id,
        selectedRecipeUrn,
      }
    },
    [applyOverride, deliveryDate, menuId],
  )

  const selectOverride = useCallback(
    async (originalRecipeId: RecipeUUID, recipeId: RecipeUUID, numPortions: number) => {
      if (overrides[originalRecipeId]?.recipeTile.id === recipeId) {
        return
      }

      const requestId = (refreshRequestIdRef.current.get(originalRecipeId) ?? 0) + 1
      refreshRequestIdRef.current.set(originalRecipeId, requestId)
      const requestContextKey = contextKey

      try {
        const preparedOverride = await prepareOverride(originalRecipeId, recipeId, numPortions)

        if (
          contextKeyRef.current !== requestContextKey ||
          requestId !== refreshRequestIdRef.current.get(originalRecipeId)
        ) {
          return
        }

        preparedOverride.commit()
      } catch {
        datadogRum.addAction('menu_experience_recipe_tile_refresh_failed', {
          menu_id: menuId,
          recipe_id: recipeId,
          num_portions: numPortions,
        })
      }
    },
    [contextKey, menuId, overrides, prepareOverride],
  )

  const reconcileAfterRemoval = useCallback(
    (recipeId: RecipeUUID) => {
      if (getQuantitiesForRecipeId(recipeId) <= 1) {
        removeOverride(recipeId)
      }
    },
    [getQuantitiesForRecipeId, removeOverride],
  )

  const getRecipeGridItem = useCallback(
    (recipe: StandardRecipeItem) => overrides[recipe.id]?.recipeTile ?? recipe,
    [overrides],
  )

  const getSelectedRecipeIdentifiers = useCallback(
    (originalRecipeId: RecipeUUID, originalRecipeUrn?: RecipeURN) => {
      const selectedRecipe = overrides[originalRecipeId]?.recipeTile

      return selectedRecipe
        ? { recipeId: selectedRecipe.id, recipeUrn: selectedRecipe.urn }
        : { recipeId: originalRecipeId, recipeUrn: originalRecipeUrn }
    },
    [overrides],
  )

  return {
    applyOverride,
    getRecipeGridItem,
    getSelectedRecipeIdentifiers,
    prepareOverride,
    reconcileAfterRemoval,
    refreshOverride,
    selectOverride,
  }
}
